import React, { ChangeEvent } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Box, Divider, Grid, SelectChangeEvent } from '@mui/material';
import {
  addElementIdToRecipient,
  removeElementIdFromRecipients,
  selectedElement,
  updateContentElementById,
} from 'features/document/documentSlice';
import { AzulCheckbox, AzulDropdownMenu, AzulTextField } from '@the-ksquare-group/azul-ds';
import { isTextFieldElement } from 'utils/typeGuards';
import RecipientSelect from 'components/RecipientSelect/RecipientSelect';
import { selectRecipients } from 'features/document/documentSlice';
import { Subtitle, Title } from '../../Sidebar.styled';
import { colorPalette } from 'styles/partials/colors';

interface Props {
  currentElement: selectedElement;
}

const styleOptions = {
  labelFont: [
    { label: 'Roboto', value: 'Roboto' },
    { label: 'Calibri', value: 'Calibri' },
    { label: 'Courier', value: 'Courier' },
  ],
  labelFontSize: [
    { label: '12px', value: '12px' },
    { label: '13px', value: '13px' },
    { label: '14px', value: '14px' },
    { label: '15px', value: '15px' },
    { label: '16px', value: '16px' },
    { label: '17px', value: '17px' },
    { label: '18px', value: '18px' },
    { label: '19px', value: '19px' },
    { label: '20px', value: '20px' },
    { label: '22px', value: '22px' },
  ],
};

const TextFieldSidebar: React.FC<Props> = ({ currentElement }) => {
  if (!isTextFieldElement(currentElement.data)) {
    return null;
  }

  const dispatch = useAppDispatch();
  const { labelColor, labelFont, labelFontSize } = currentElement.data.style;
  const { label, placeholder, required } = currentElement.data.content;

  const {
    data: { recipient: currentRecipient },
  } = currentElement;

  const recipients = useAppSelector(selectRecipients);

  const handleOnChangeRecipient = (event: SelectChangeEvent) => {
    dispatch(removeElementIdFromRecipients(currentElement.data.id));

    const selectedRecipient = recipients.find(recipient => recipient.fullName === event.target.value);
    if (selectedRecipient) {
      dispatch(addElementIdToRecipient({ recipientId: selectedRecipient.id, elementId: currentElement.data.id }));

      dispatch(
        updateContentElementById({
          content: {},
          style: {},
          recipient: selectedRecipient,
        })
      );
    }
  };

  const handleUpdateByProperty = (property: string) => {
    return (event: ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>) => {
      dispatch(
        updateContentElementById({
          content: {
            [property]:
              property === 'required' ? (event as ChangeEvent<HTMLInputElement>).target.checked : event.target.value,
          },
          style: {},
        })
      );
    };
  };

  const handleUpdateByStyle = (style: string) => {
    return (event: ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>) => {
      dispatch(
        updateContentElementById({
          content: {},
          style: {
            [style]: event.target.value,
          },
        })
      );
    };
  };

  return (
    <Grid container direction='row' gap={2}>
      <Grid item xs={12}>
        <Title>Text Field Block</Title>
      </Grid>

      <Box display={'flex'} flexDirection={'column'} gap={'20px'} marginTop={'24px'} width='100%'>
        <Subtitle>Recipient</Subtitle>
        <RecipientSelect
          value={currentRecipient}
          recipientOptions={recipients}
          editRecipients={false}
          onChange={handleOnChangeRecipient}
        />
      </Box>
      <Divider
        color='#C9CACB'
        flexItem
        orientation='horizontal'
        variant='middle'
        sx={{ margin: '10px 0', width: '100%' }}
      />
      <Grid item xs={12}>
        <Subtitle>Label Text</Subtitle>
        <AzulTextField
          type='text'
          label=''
          placeholder='Add Text'
          name='labelInput'
          value={label}
          onChange={handleUpdateByProperty('label')}
        />
      </Grid>

      <Grid item xs={12}>
        <Subtitle>Label Font</Subtitle>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <AzulDropdownMenu
              options={styleOptions.labelFontSize}
              value={labelFontSize}
              onChange={handleUpdateByStyle('labelFontSize')}
            />
          </Grid>
          <Grid item xs={6}>
            <AzulDropdownMenu
              options={styleOptions.labelFont}
              value={labelFont}
              onChange={handleUpdateByStyle('labelFont')}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid direction='column' display='flex' item xs={12}>
        <Subtitle>Label Color</Subtitle>
        <input type='color' value={labelColor} onChange={handleUpdateByStyle('labelColor')} />
      </Grid>

      <Grid color={colorPalette.darkGray1} item xs={12}>
        <AzulCheckbox value={required} label='Required' onChange={handleUpdateByProperty('required')} />
      </Grid>

      <Grid item xs={12}>
        <Subtitle>Placeholder</Subtitle>
        <AzulTextField
          type='text'
          label=''
          placeholder='Add Text'
          name='placeholderInput'
          value={placeholder}
          onChange={handleUpdateByProperty('placeholder')}
        />
      </Grid>
    </Grid>
  );
};

export default TextFieldSidebar;
